'use client'
import { useQuery } from '@tanstack/react-query'
import {
    HiOutlineArrowDownTray,
    HiOutlineArrowsRightLeft,
    HiOutlineCheck,
    HiOutlineClipboardDocumentList,
    HiOutlineClock,
    HiOutlineDocumentText,
    HiOutlineMagnifyingGlass,
    HiOutlineMegaphone,
    HiOutlineXMark,
} from 'react-icons/hi2'
import CustomFetch from '@/Utils/APIRequest/AxiosInstance'
import { PermissionAPI } from '@/Utils/APIRequest/Endpoints'
import { useTranslation } from '@/Utils/Hooks/useTranslate'
import { formatNumberWithCommas } from '@/Utils/Other/FormatNumberWithCommas'
import Loader from '@/Layout/Loader'
import NoData from '@/Component/Common/Tables/NoData/NoData'

const BOOLEAN_PERMISSION_TYPES = new Set(['campaign_report', 'barter_allowed'])
const LIMIT_ONLY_PERMISSION_TYPES = new Set(['browse_influencers', 'export_influencer_limit'])

const formatPermissionLabel = (type) =>
    String(type || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())

const getPermissionIcon = (type: string) => {
    switch (type) {
        case 'campaign_report':
            return HiOutlineDocumentText
        case 'barter_allowed':
            return HiOutlineArrowsRightLeft
        case 'export_influencer_limit':
            return HiOutlineArrowDownTray
        case 'browse_influencers':
            return HiOutlineMagnifyingGlass
        case 'number_of_campaigns':
            return HiOutlineMegaphone
        case 'export_influencer_time_limit_time_period':
            return HiOutlineClock
        case 'work_order':
            return HiOutlineClipboardDocumentList
        case 'browse_influencers_time_period':
            return HiOutlineClock
        default:
            return HiOutlineClipboardDocumentList
    }
}

const isPermissionAllowed = (item) => Number(item?.limit) > 0

const Permissions = () => {
    const { translations } = useTranslation()

    const { data: PermissionData, isPending } = useQuery({
        queryKey: [PermissionAPI, 'showPermissions'],
        queryFn: () => CustomFetch({ url: PermissionAPI }, null, false),
        refetchOnWindowFocus: false,
        select: (data) => data?.data?.showPermissions ?? data?.data?.data?.showPermissions ?? [],
    })

    if (isPending) return <Loader />

    return (
        <>
            <h2 className="text-primary text-xl mb-7 font-medium leading-6">🔑 {translations?.permissions}</h2>
            {PermissionData?.length > 0 ? (
                <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                    {PermissionData.map((item) => {
                        const type = item.type
                        const title = translations?.[type] || formatPermissionLabel(type)
                        const Icon = getPermissionIcon(type)
                        const isBoolean = BOOLEAN_PERMISSION_TYPES.has(type)

                        if (isBoolean) {
                            const allowed = isPermissionAllowed(item)
                            const descriptionKey = `${type}_desc`
                            const description =
                                translations?.[descriptionKey] ||
                                (type === 'campaign_report'
                                    ? 'Permission to view campaign reports'
                                    : 'Permission to create barter campaigns')

                            return (
                                <div
                                    key={item.id}
                                    className={`rounded-2xl border p-4 ${allowed
                                            ? 'bg-success/10 border-success/20'
                                            : 'bg-danger/10 border-danger/20'
                                        }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <span
                                                className={`mt-0.5 shrink-0 ${allowed ? 'text-success' : 'text-danger'
                                                    }`}
                                            >
                                                <Icon className="w-5 h-5" />
                                            </span>
                                            <div className="min-w-0">
                                                <h3 className="text-base font-semibold text-black leading-6">
                                                    {title}
                                                </h3>
                                                <p className="text-xs text-darkGray mt-0.5 leading-5">
                                                    {description}
                                                </p>
                                                <span
                                                    className={`inline-flex items-center gap-1 shrink-0 rounded-full px-2.5 py-1 text-xs mt-3 font-semibold text-white ${allowed ? 'bg-success' : 'bg-danger'
                                                        }`}
                                                >
                                                    {allowed ? (
                                                        <HiOutlineCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                                                    ) : (
                                                        <HiOutlineXMark className="w-3.5 h-3.5" strokeWidth={2.5} />
                                                    )}
                                                    {allowed
                                                        ? translations?.allowed || 'Allowed'
                                                        : translations?.notAllowed || 'Not Allowed'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        const showUsageStats = !LIMIT_ONLY_PERMISSION_TYPES.has(type)

                        return (
                            <div
                                key={item.id}
                                className="border border-lightGray rounded-2xl p-4 bg-white"
                            >
                                <div className="flex items-center gap-2.5 mb-4">
                                    <span className="text-primary shrink-0">
                                        <Icon className="w-5 h-5" />
                                    </span>
                                    <h3 className="text-base font-semibold text-black leading-6">
                                        {title}
                                    </h3>
                                </div>
                                <div className={`grid gap-3 ${showUsageStats ? 'grid-cols-3' : 'grid-cols-1'}`}>
                                    <div>
                                        <span className="block text-xs text-darkGray mb-0.5">
                                            {translations?.limit}
                                        </span>
                                        <span className="font-semibold text-black">
                                            {formatNumberWithCommas(item?.limit)}
                                        </span>
                                    </div>
                                    {showUsageStats && (
                                        <>
                                            <div>
                                                <span className="block text-xs text-darkGray mb-0.5">
                                                    {translations?.used}
                                                </span>
                                                <span className="font-semibold text-black">
                                                    {formatNumberWithCommas(item?.used)}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="block text-xs text-darkGray mb-0.5">
                                                    {translations?.remaining}
                                                </span>
                                                <span className="font-semibold text-black">
                                                    {formatNumberWithCommas(item?.remaining)}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <NoData />
            )}
        </>
    )
}

export default Permissions
